/**
 * Message reactions live on `message.reactions` as an array of
 * `{ party, emoji, source, byUserId, at }` entries, at most one per party slot. WhatsApp shows one
 * reaction per sender and the whole team shares a single "business" identity, so a 1:1 chat has
 * only two possible slots: the patient's reaction and the shared business reaction. WhatsApp GROUP
 * messages additionally carry `party: "participant"` entries — one slot per group member, keyed by
 * `senderJid` — while the single business slot stays shared (staff + the linked phone) and
 * `patient` never appears; {@link groupReactionsByEmoji} folds that multi-party shape into chips.
 */

/** Fixed WhatsApp-style quick reaction bar (matches the backend's allowed set). */
export const REACTION_QUICK_SET = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/**
 * Normalize a message's `reactions` field into an array of
 * `{ party, emoji, source, byUserId, at }`. Tolerates an array, a JSON string, or absence.
 * @param {object} message
 * @returns {Array<{ party: string, emoji: string, source?: string, byUserId?: string, at?: string }>}
 */
export function normalizeReactions(message) {
  const raw = message?.reactions;
  if (!raw) return [];
  let list = raw;
  if (typeof raw === "string") {
    try {
      list = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list.filter((r) => r && typeof r.emoji === "string" && r.emoji);
}

/** The single shared business reaction on a message, or null. */
export function businessReaction(message) {
  return normalizeReactions(message).find((r) => r.party === "business") || null;
}

/** The patient's reaction on a message, or null. */
export function patientReaction(message) {
  return normalizeReactions(message).find((r) => r.party === "patient") || null;
}

/**
 * Fold a WhatsApp GROUP message's multi-party reactions into one entry per emoji, in first-seen
 * order (stable across re-renders — the server appends slots, it never reorders them).
 * `mine` marks the emoji held by the shared business slot, which is the only one the UI can
 * toggle; `reactors` keeps every contributing slot so a chip can list who reacted
 * (`senderJid` → roster name for participants, `byUserId` → member directory for staff).
 * @param {object} message
 * @returns {Array<{ emoji: string, count: number, mine: boolean,
 *   reactors: Array<{ party: string, byUserId: string, senderJid: string, at: string }> }>}
 */
export function groupReactionsByEmoji(message) {
  const byEmoji = new Map();
  for (const r of normalizeReactions(message)) {
    let slot = byEmoji.get(r.emoji);
    if (!slot) {
      slot = { emoji: r.emoji, count: 0, mine: false, reactors: [] };
      byEmoji.set(r.emoji, slot);
    }
    slot.count += 1;
    if (r.party === "business") {
      slot.mine = true;
    }
    slot.reactors.push({
      party: String(r.party || ""),
      byUserId: String(r.byUserId || ""),
      senderJid: String(r.senderJid || ""),
      at: r.at || "",
    });
  }
  return [...byEmoji.values()];
}
